import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Box, Fade, Stack, OutlinedInput, InputAdornment, Button, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { FeedbackContext } from '../../../App';
import DisplayTable from '../../utility/DisplayTable.jsx';
import { tableClasses, roomDetailClasses } from '../../utility/constants.jsx';
import { MetrcLogo } from '../../utility';
import APIService from '../../../api/apiService';

const tableTitles = [
  { title: 'Sl. No' },
  { title: 'State' },
];

export default function LabTestStatesTab(props) {
  const { currDarkThemeClass = '', feedbackCX } = props;
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [syncLoading, setSyncLoading] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(0);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const fetchStates = async (currentPage = page, limit = rowsPerPage, search = debouncedSearchTerm) => {
    setLoading(true);
    try {
      let url = `labtest-states/?limit=${limit}&page=${currentPage + 1}`;
      if (search) {
        url += `&state_name__icontains=${encodeURIComponent(search)}`;
      }
      const response = await APIService.fetchInstance(url);
      if (response?.data) {
        setStates(response.data.results || []);
        setTotalCount(response.data.count || 0);
      }
    } catch (err) {
      if (feedbackCX) {
        feedbackCX.setContextValue(true, {
          type: 'error',
          message: 'Failed to fetch lab test states.',
        });
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchStates(page, rowsPerPage, debouncedSearchTerm);
    // eslint-disable-next-line
  }, [page, rowsPerPage, debouncedSearchTerm]);

  const handlePageChange = (newPage, newRowsPerPage) => {
    if (rowsPerPage !== newRowsPerPage) {
      setPage(0);
      setRowsPerPage(newRowsPerPage);
    } else {
      setPage(newPage);
    }
  };

  const handleSearchChange = event => {
    setSearchTerm(event.target.value);
  };

  const handleSyncFromMetrc = async () => {
    setSyncLoading(true);
    try {
      await APIService.fetchInstance('labtest-states/?sync_to_metrc=true');
      await fetchStates(page, rowsPerPage, debouncedSearchTerm);
      if (feedbackCX) {
        feedbackCX.setContextValue(true, {
          type: 'success',
          message: 'Synced from Metrc successfully!',
        });
      }
    } catch (err) {
      if (feedbackCX) {
        feedbackCX.setContextValue(true, {
          type: 'error',
          message: 'Sync from Metrc failed.',
        });
      }
    }
    setSyncLoading(false);
  };

  const outlinedInputProps = {
    className: `${roomDetailClasses.filterInput} ${currDarkThemeClass}`,
    size: 'small',
    placeholder: 'Search using State Name',
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon />
      </InputAdornment>
    ),
    onChange: handleSearchChange,
    value: searchTerm,
  };

 
  const tableEntries = useMemo(
    () => states.map((s, idx) => [
      `${idx + 1 + page * rowsPerPage}`,
      s.state_name,
    ]),
    [states, page, rowsPerPage]
  );

  return (
    <Fade in={true} timeout={2000}>
      <Box>
        <Stack direction="row" justifyContent="flex-end" padding={2}>
          <Button
            onClick={handleSyncFromMetrc}
            disabled={syncLoading}
            style={{ display: 'flex', alignItems: 'center', paddingRight: '20px' }}
            sx={{ color: '#B1D23A', '&.Mui-disabled': { color: '#B1D23A' } }}
          >
            <MetrcLogo style={{ width: 24, height: 24, marginRight: 8 }} />
            {syncLoading ? 'Syncing...' : 'Sync From Metrc'}
          </Button>
          <OutlinedInput {...outlinedInputProps} />
        </Stack>
        <Box className={`${tableClasses?.container} ${currDarkThemeClass}`}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress />
            </Box>
          ) : (
            <DisplayTable
              tableTitles={tableTitles}
              tableEntries={tableEntries}
              currDarkThemeClass={currDarkThemeClass}
              toShowAddBtn={false}
              page={page}
              rowsPerPage={rowsPerPage}
              totalCount={totalCount}
              onPageChange={handlePageChange}
              emptyTableMsg="No Lab Tests State Found"
              entity="Lab Test States"
            />
          )}
        </Box>
      </Box>
    </Fade>
  );
}